import cv from "../../images/cv-icon.png";
import github from "../../images/github.png";
import linkedin from "../../images/linkedin-logo.png";
import Office from "../../images/office-icon.png";
import Languages from "../../images/languages-icon.png";
import VideoIntro from "../../images/video-icon-green.png";

const languageLogos = {
  JavaScript: "/images/coding_logo/javascript.png",
  Python: "/images/coding_logo/python.png",
  C: "/images/coding_logo/c.png",
  Swift: "/images/coding_logo/swift.png",
  "C#": "/images/coding_logo/csharp.png",
  "C++": "/images/coding_logo/cplusplus.png",
  Java: "/images/coding_logo/java.svg",
  Go: "/images/coding_logo/go.png",
  PHP: "/images/coding_logo/php.svg",
  Ruby: "/images/coding_logo/ruby.webp",
  Kotlin: "/images/coding_logo/kotlin.png",
  Rust: "/images/coding_logo/rust.png",
  // Newly added languages
  Clojure: "/images/coding_logo/clojure.png",
  Haskell: "/images/coding_logo/haskell.png",
  Dart: "/images/coding_logo/dart.png",
  Elixir: "/images/coding_logo/elixir.webp",
  "Objective-C": "/images/coding_logo/objc.svg",
  Perl: "/images/coding_logo/perl.png",
  Scala: "/images/coding_logo/scala.png",
  Typescript: "/images/coding_logo/typescript.png",
  React: "/images/coding_logo/react.png",
  Vue: "/images/coding_logo/vue.png",
  Angular: "/images/coding_logo/angular.png",
  "Next.js": "/images/coding_logo/next-js.svg",
};

// Helper to format the displayed duration string
function formatDuration(yrs, mos) {
  const y = parseInt(yrs, 10);
  const m = parseInt(mos, 10);

  // No data -> show nothing
  if ((!y || y === 0) && (!m || m === 0)) {
    return ""; 
  }

  // If we have both yrs and mos and yrs > 0 and mos > 0
  if (y > 0 && m > 0) {
    return `(${y} yrs and ${m} mos)`;
  }
  // If only years is > 0
  if (y > 0) {
    return `(${y} yrs)`;
  }
  // If only months is > 0
  if (m > 0) {
    return `(${m} mos)`;
  }
  // If user typed 0 or blank
  return "";
}

const ProfilePreview = ({ candidate, loading }) => {
  if (loading) {
    return (
      <div className="py-20">
        <div className="flex flex-col rounded-xl bg-white items-center h-[70vh]">
          <div className="max-w-[400px] flex flex-col rounded-xl bg-white animate-pulse p-4">
            <div className="h-72 max-w-[400px] bg-gray-300 rounded-xl"></div>
            <div className="h-10 w-48 bg-gray-300 rounded mt-2"></div>
            <div className="flex h-6 mt-2">
              <div className="h-6 w-20 mr-2 bg-gray-300 rounded"></div>
              <div className="h-6 w-24 mr-2 bg-gray-300 rounded"></div>
              <div className="h-6 w-24 mr-2 bg-gray-300 rounded"></div>
            </div>
            <div className="flex h-6 mt-2">
              <div className="h-6 w-60 mr-2 bg-gray-300 rounded"></div>
              <div className="h-6 w-60 mr-2 bg-gray-300 rounded"></div>
            </div>
            <div className="h-6 w-full bg-gray-300 rounded mt-2"></div>
            <div className="h-6 w-80 bg-gray-300 rounded mt-2"></div>
            <div className="h-6 w-full bg-gray-300 rounded mt-2"></div>
            <div className="h-6 w-80 bg-gray-300 rounded mt-2"></div>
            <div className="h-6 w-full bg-gray-300 rounded mt-2"></div>
            <div className="h-6 w-full bg-gray-300 rounded mt-2"></div>
          </div>
        </div>
      </div>
    );
  }

  const getColorClasses = (stream) => {
    switch (stream) {
      case "Generalist":
        return {
          bgColor: "bg-emerald-100",
          textColor: "text-emerald-700",
          workBgColor: "bg-emerald-50",
          fillColor: "fill-emerald-700",
          bgColor2: "bg-emerald-400",
          borderColor: "border-emerald-400",
        };
      case "Sales":
        return {
          bgColor: "bg-blue-100",
          textColor: "text-blue-700",
          workBgColor: "bg-blue-50",
          fillColor: "fill-blue-700",
          bgColor2: "bg-blue-400",
          borderColor: "border-blue-400",
        };
      case "Software Engineering":
        return {
          bgColor: "bg-orange-100",
          textColor: "text-orange-700",
          workBgColor: "bg-orange-50",
          fillColor: "fill-orange-700",
          bgColor2: "bg-orange-400",
          borderColor: "border-orange-400",
        };
      case "Marketing":
        return {
          bgColor: "bg-purple-100",
          textColor: "text-purple-700",
          workBgColor: "bg-purple-50",
          fillColor: "fill-purple-700",
          bgColor2: "bg-purple-400",
          borderColor: "border-purple-400",
        };
      default:
        return {
          bgColor: "bg-gray-100",
          textColor: "text-gray-700",
          workBgColor: "bg-gray-50",
          fillColor: "fill-gray-700",
          bgColor2: "bg-gray-400",
          borderColor: "border-gray-400",
        };
    }
  };

  const { bgColor, textColor, workBgColor, fillColor, bgColor2, borderColor } =
    getColorClasses(candidate.stream);

  const workingStyleMapping = candidate.working_style?.length
    ? candidate.working_style.map((style) => {
        if (style === "5 days a week in office") {
          return "Fully in office";
        } else if (style === "Hybrid (1-4 days in office)") {
          return "Hybrid";
        } else if (style === "Fully remote") {
          return "Fully remote";
        }
        return style;
      })
    : [];

  return (
    <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
      <div className="flex relative flex-col items-center bg-white rounded-xl p-4">
        <div className="w-full flex flex-col h-full bg-white gap-3">
          <div className="relative">
            <img
              src={candidate.image}
              className="h-72 w-full object-cover rounded-xl bg-[var(--overlay-2)]"
              alt={candidate.name}
            />
          </div>
          <div className="flex flex-col flex-grow justify-between">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <h5 className="text-xl font-bold tracking-tight text-gray-900">
                  {candidate.name}
                </h5>

                {candidate.linkedin && (
                  <img
                    src={linkedin}
                    alt={candidate.name}
                    title="Linkedin icon"
                    className="h-5 w-5 ml-2 cursor-pointer"
                    onClick={() => {
                      window.open(candidate.linkedin, "_blank");
                    }}
                  />
                )}

                {candidate.cv && (
                  <img
                    src={cv}
                    alt="CV icon"
                    title="CV icon"
                    className="h-5 w-5 ml-2 cursor-pointer"
                    onClick={() => {
                      window.open(candidate.cv, "_blank");
                    }}
                  />
                )}

                {candidate.video && (
                  <img
                    src={VideoIntro}
                    alt="Video intro"
                    title="Video Intro icon"
                    className="h-6 w-6 ml-2 cursor-pointer"
                    onClick={() => {
                      window.open(candidate.video, "_blank");
                    }}
                  />
                )}

                {candidate.github && (
                  <img
                    src={github}
                    alt="Github icon"
                    title="Github icon"
                    className="h-5 w-5 ml-2 cursor-pointer"
                    onClick={() => {
                      window.open(candidate.github, "_blank");
                    }}
                  />
                )}


{candidate.marketing_portfolio && (
              <svg
              onClick={() => {
                window.open(candidate.marketing_portfolio, "_blank");
              }}
          
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6 cursor-pointer ml-2"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
              />
            </svg>
)}




              </div>

              <div className="flex flex-col text-right text-sm">
                Expected Salary Range:
                {candidate.expectedSalaryRange && (
                  <h5 className="text-sm font-semibold tracking-tight text-gray-900">
                    {candidate.expectedSalaryRange}
                  </h5>
                )}
                {candidate.minimum_salary && (
                  <h5 className="text-sm font-semibold tracking-tight text-gray-900">
                    {candidate.minimum_salary} - {candidate.ideal_salary}
                    {candidate.commission_expectation &&
                      ` + ${candidate.commission_expectation} commission`}
                  </h5>
                )}
              </div>
            </div>
            <div className="pt-2">
              {candidate.roles &&
                candidate.roles.map((role, index) => (
                  <span
                    key={index}
                    className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-sm font-medium ${bgColor} ${textColor}`}
                  >
                    {role}
                  </span>
                ))}
              {candidate.sales && (
                <span
                  className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-sm font-medium ${bgColor} ${textColor}`}
                >
                  {candidate.sales}
                </span>
              )}
              {candidate.swe_roles &&
                candidate.swe_roles.map((role, index) => (
                  <span
                    key={index}
                    className={`inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-sm font-medium ${bgColor} ${textColor}`}
                  >
                    {role}
                  </span>
                ))}
              {candidate.time_coding && (
                <span
                  className={`inline-flex mr-2 items-center gap-x-1.5 rounded-md px-2 py-1 text-sm md:text-sm font-medium ${bgColor} ${textColor}`}
                >
                  {candidate.time_coding} of coding exp
                </span>
              )}
            </div>
            <div className="inline-flex mr-2 mb-2 items-center gap-x-1.5 rounded-md text-black text-sm md:text-md font-medium flex gap-x-2">
              {candidate.coding_languages &&
                candidate.coding_languages.map((language, index) => {
                  const trimmedLang = language.trim();
                  return (
                    <img
                      key={`${trimmedLang}-${index}`}
                      src={languageLogos[trimmedLang] || ""}
                      alt={trimmedLang}
                      className="h-6 w-6"
                    />
                  );
                })}
            </div>

            {/* Working style + spoken languages */}
            <div className="flex">
              {candidate.working_style && (
                <p className="pr-5 font-normal flex text-sm text-black items-center pb-2 pt-1">
                  <img className="h-6 pr-1" src={Office} alt="Office icon" />
                  {workingStyleMapping.length === 3
                    ? "Any working style"
                    : workingStyleMapping.join(" or ")}
                </p>
              )}

              {candidate.languages &&
                candidate.languages.length > 0 &&
                !(
                  candidate.languages.length === 1 &&
                  candidate.languages[0] === "N/A"
                ) && (
                  <p className="font-normal flex text-sm text-black items-center pb-2 pt-1">
                    <img
                      className="h-6 pr-2"
                      src={Languages}
                      alt="Languages icon"
                    />
                    {candidate.languages
                      .filter((lang) => lang !== "N/A")
                      .join(", ")}
                  </p>
                )}
            </div>

            {/* Undergrad Education */}
            {candidate.undergrad_degree_type && (
              <p className="text-sm">
                <span className="font-semibold">
                  {candidate.undergrad_degree_type}{" "}
                  {candidate.undergrad_degree_subject}
                </span>{" "}
                @ {candidate.undergrad_university} ({candidate.undergrad_result} -{" "}
                {candidate.undergrad_graduation_year})
              </p>
            )}

            {/* Postgrad Education */}
            {candidate.postgrad_degree_type && (
              <p className="text-sm pt-2">
                <span className="font-semibold">
                  {candidate.postgrad_degree_type}{" "}
                  {candidate.postgrad_degree_subject}
                </span>{" "}
                @ {candidate.postgrad_university} ({candidate.postgrad_result} -{" "}
                {candidate.postgrad_graduation_year})
              </p>
            )}

            {/* Work Experience Section */}
            <div className={`pb-2 mt-2 px-2 rounded-md ${workBgColor}`}>
              {candidate.recentTarget && (
                <div className="font-normal text-sm text-gray-700 pt-2">
                  <span className="font-semibold text-black">
                    Recent Target Achieved:
                  </span>{" "}
                  {candidate.recentTarget}
                </div>
              )}

              {/* Primary Work Experience */}
              {candidate.work_experience_1_company && (
                <>
                  <p className="text-sm pt-2">
                    <span className="font-semibold">
                      {candidate.work_experience_1_role}
                    </span>{" "}
                    @ {candidate.work_experience_1_company}{" "}
                    {formatDuration(
                      candidate.work_experience_1_duration_yrs,
                      candidate.work_experience_1_duration_mos
                    )}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {candidate.work_experience_1_description}
                  </p>
                </>
              )}

              {/* Secondary Work Experience */}
              {candidate.work_experience_2_company && (
                <>
                  <p className="text-sm pt-2">
                    <span className="font-semibold">
                      {candidate.work_experience_2_role}
                    </span>{" "}
                    @ {candidate.work_experience_2_company}{" "}
                    {formatDuration(
                      candidate.work_experience_2_duration_yrs,
                      candidate.work_experience_2_duration_mos
                    )}
                  </p>
                  <p className="text-gray-500 text-sm">
                    {candidate.work_experience_2_description}
                  </p>
                </>
              )}
            </div>

            {/* Top Achievement */}
            {candidate.top_achievement && (
              <p className="mt-2 font-normal text-sm text-gray-700 flex">
                <div>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.7"
                    stroke="currentColor"
                    className="size-5 mr-1"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M16.5 18.75h-9m9 0a3 3 0 0 1 3 3h-15a3 3 0 0 1-3-3m9 0v-3.375c0-.621-.503-1.125-1.125-1.125h-.871M7.5 18.75v-3.375c0-.621.504-1.125 1.125-1.125h.872m5.007 0H9.497m5.007 0a7.454 7.454 0 0 1-.982-3.172M9.497 14.25a7.454 7.454 0 0 0 .981-3.172M5.25 4.236c-.982.143-1.954.317-2.916.52A6.003 6.003 0 0 0 7.73 9.728M5.25 4.236V4.5c0 2.108.966 3.99 2.48 5.228M5.25 4.236V2.721C7.456 2.41 9.71 2.25 12 2.25c2.291 0 4.545.16 6.75.47v1.516M7.73 9.728a6.726 6.726 0 0 0 2.748 1.35m8.272-6.842V4.5c0 2.108-.966 3.99-2.48 5.228m2.48-5.492a46.32 46.32 0 0 1 2.916.52 6.003 6.003 0 0 1-5.395 4.972m0 0a6.726 6.726 0 0 1-2.749 1.35m0 0a6.772 6.772 0 0 1-3.044 0"
                    />
                  </svg>
                </div>
                {candidate.top_achievement}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePreview;
